// GallerySection.jsx
import { useState, useEffect } from 'react';
import { roomImages } from '../../data/roomImages';

// ── data ──────────────────────────────────────────────────────────
const galleryImages = roomImages.shrileela.gallery
// Each item is expected to be either a URL string OR
// an object { url, alt, category } — both shapes are handled.

// ── helpers ───────────────────────────────────────────────────────
const getSrc      = (img) => (typeof img === 'string' ? img : img.url);
const getAlt      = (img, i) => (typeof img === 'string' ? `Gallery ${i + 1}` : img.alt);
const getCategory = (img) => (typeof img === 'string' ? null : img.category);

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Jost:wght@300;400;500;600&display=swap');

  .gallery-section { padding: 100px 24px; background: #f8faf7; font-family: 'Jost', sans-serif; }
  .gallery-inner { max-width: 1200px; margin: 0 auto; }
  .gallery-header { text-align: center; margin-bottom: 48px; }
  .section-eyebrow {
    display: inline-block; background: #e8f0e8; color: #3d5a3e;
    font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase;
    padding: 6px 18px; border-radius: 40px; margin-bottom: 20px;
  }
  .section-title {
    font-family: 'Playfair Display', serif; font-size: clamp(32px, 5vw, 52px);
    font-weight: 700; color: #1a2b1a; margin: 0 0 16px;
  }
  .gallery-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .gallery-item {
    position: relative; border-radius: 14px; overflow: hidden;
    aspect-ratio: 1/1; cursor: pointer;
  }
  .img-shimmer {
    position: absolute; inset: 0;
    background: linear-gradient(90deg, #e8f0e8 25%, #d4e6d4 50%, #e8f0e8 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    transition: opacity 0.5s ease;
  }
  @keyframes shimmer { 0% { background-position: 200% 0 } 100% { background-position: -200% 0 } }
  .gallery-img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform 0.6s ease, opacity 0.55s ease;
  }
  .gallery-item:hover .gallery-img { transform: scale(1.1); }
  .gallery-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(26,43,26,0.8) 0%, transparent 60%);
    opacity: 0; transition: opacity 0.3s;
    display: flex; align-items: flex-end; padding: 16px;
  }
  .gallery-item:hover .gallery-overlay { opacity: 1; }
  .gallery-caption { color: #fff; font-size: 13px; font-weight: 500; }
  .gallery-cat-tag {
    display: inline-block; background: rgba(168,213,162,0.3);
    color: #a8d5a2; font-size: 11px; font-weight: 600;
    padding: 3px 10px; border-radius: 40px; margin-bottom: 6px;
  }

  /* Lightbox */
  .lightbox-backdrop {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(0,0,0,0.92); backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center;
    animation: lbFadeIn 0.25s ease;
  }
  .lb-image {
    max-width: 88vw; max-height: 82vh; object-fit: contain;
    border-radius: 4px; box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    animation: lbZoomIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  .lb-btn {
    position: absolute; background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.15); color: #fff;
    border-radius: 50%; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s;
  }
  .lb-btn:hover { background: rgba(255,255,255,0.18); }
  .lb-close { top: 20px; right: 24px; width: 40px; height: 40px; font-size: 18px; }
  .lb-prev  { left: 20px;  top: 50%; transform: translateY(-50%); width: 48px; height: 48px; font-size: 22px; }
  .lb-next  { right: 20px; top: 50%; transform: translateY(-50%); width: 48px; height: 48px; font-size: 22px; }
  .lb-counter {
    position: absolute; bottom: 60px; left: 50%; transform: translateX(-50%);
    color: rgba(255,255,255,0.5); font-size: 13px; letter-spacing: 2px;
  }
  .lb-thumbs {
    position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%);
    display: flex; gap: 6px;
  }
  .lb-thumb {
    width: 44px; height: 30px; object-fit: cover;
    opacity: 0.35; cursor: pointer; transition: opacity 0.2s;
  }
  .lb-thumb.active { opacity: 1; outline: 1.5px solid #b8976a; }

  @keyframes lbFadeIn { from { opacity: 0 } to { opacity: 1 } }
  @keyframes lbZoomIn { from { transform: scale(0.92); opacity: 0 } to { transform: scale(1); opacity: 1 } }

  @media (max-width: 900px) { .gallery-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 500px) { .gallery-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; } }
`;

// ── GalleryImage ──────────────────────────────────────────────────
const GalleryImage = ({ img, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const src      = getSrc(img);
  const alt      = getAlt(img, index);
  const category = getCategory(img);

  return (
    <div className="gallery-item" onClick={() => onClick(index)}>
      <div className="img-shimmer" style={{ opacity: loaded ? 0 : 1 }} />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        className="gallery-img"
        style={{ opacity: loaded ? 1 : 0 }}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
      <div className="gallery-overlay">
        <div>
          {category && <span className="gallery-cat-tag">{category}</span>}
          <div className="gallery-caption">{alt}</div>
        </div>
      </div>
    </div>
  );
};

// ── Lightbox ──────────────────────────────────────────────────────
const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext, onJump }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft')  onPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="lightbox-backdrop" onClick={onClose}>
      <button className="lb-btn lb-close" onClick={onClose}>✕</button>
      <button className="lb-btn lb-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }}>‹</button>

      <img
        src={getSrc(images[currentIndex])}
        alt={getAlt(images[currentIndex], currentIndex)}
        className="lb-image"
        onClick={(e) => e.stopPropagation()}
      />

      <button className="lb-btn lb-next" onClick={(e) => { e.stopPropagation(); onNext(); }}>›</button>

      <div className="lb-counter">{currentIndex + 1} / {images.length}</div>

      <div className="lb-thumbs" onClick={(e) => e.stopPropagation()}>
        {images.map((img, i) => (
          <img
            key={i}
            src={getSrc(img)}
            alt=""
            className={`lb-thumb ${i === currentIndex ? 'active' : ''}`}
            onClick={() => onJump(i)}
          />
        ))}
      </div>
    </div>
  );
};

// ── GallerySection ────────────────────────────────────────────────
const GallerySection = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const images = galleryImages || [];
  const isOpen = lightboxIndex !== null;

  const open  = (i) => setLightboxIndex(i);
  const close = () => setLightboxIndex(null);
  const prev  = () => setLightboxIndex((lightboxIndex - 1 + images.length) % images.length);
  const next  = () => setLightboxIndex((lightboxIndex + 1) % images.length);
  const jump  = (i) => setLightboxIndex(i);

  return (
    <>
      <style>{css}</style>

      <section id="gallery" className="gallery-section">
        <div className="gallery-inner">
          <div className="gallery-header">
            <span className="section-eyebrow">Visual Tour</span>
            <h2 className="section-title">Resort Gallery</h2>
          </div>
          <div className="gallery-grid">
            {images.map((img, i) => (
              <GalleryImage key={i} img={img} index={i} onClick={open} />
            ))}
          </div>
        </div>
      </section>

      {isOpen && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={close}
          onPrev={prev}
          onNext={next}
          onJump={jump}
        />
      )}
    </>
  );
};

export default GallerySection;